import { useEffect, useState } from "react";
import {
    ScheduleDeleteJournalEntryMode,
    ScheduleLifecycleVerb,
} from "@common/types/domains/accounting/schedule";
import { useLockDateState } from "@api/lock.api";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { VStack } from "@design-system/VStack/VStack";
import { getFirstUnlockedDateOnly, isLockedByDateOnly } from "@helpers/lock";
import { SCHEDULE_DELETE_REMOVE_LABEL } from "../change/scheduleChangeCopy";
import { SCHEDULE_OPTIONS_BACK_LABEL } from "../options/ScheduleOptionsModal";
import {
    SCHEDULE_BULK_LIFECYCLE_CONFIRM_LABELS,
    ScheduleBulkLifecycleVerb,
    formatScheduleBulkLifecycleTitle,
} from "./scheduleLifecycleBulkCopy";
import {
    clampToUnlocked,
    getScheduleResumeDefaultDate,
} from "./scheduleResumeDate";
import { ScheduleResumeDateField } from "./ScheduleResumeDateField";
import { ScheduleLifecycleBulkAction } from "./useScheduleLifecycleBulkSubmit";

export type ScheduleBulkConfirmVerb = Exclude<
    ScheduleBulkLifecycleVerb,
    ScheduleLifecycleVerb.cancel
>;

export interface ScheduleBulkConfirmTarget {
    verb: ScheduleBulkConfirmVerb;
    count: number;
    entityId: number | null;
    body: string;
    hasRatableSchedule: boolean;
    skippedWarning?: string;
}

export type ScheduleBulkConfirmation = Exclude<
    ScheduleLifecycleBulkAction,
    { verb: ScheduleLifecycleVerb.cancel }
>;

interface ScheduleLifecycleBulkConfirmModalProps {
    target: ScheduleBulkConfirmTarget | null;
    onConfirm: (confirmation: ScheduleBulkConfirmation) => Promise<void>;
    onHide: () => void;
}

function buildConfirmation(
    verb: ScheduleBulkConfirmVerb,
    resumeDate: string,
): ScheduleBulkConfirmation {
    switch (verb) {
        case ScheduleLifecycleVerb.resume:
            return { verb, effectiveDate: resumeDate };
        case ScheduleLifecycleVerb.delete:
            return {
                verb,
                journalEntryMode: ScheduleDeleteJournalEntryMode.reverse,
            };
        case ScheduleLifecycleVerb.pause:
            return { verb };
    }
}

export const ScheduleLifecycleBulkConfirmModal = ({
    target,
    onConfirm,
    onHide,
}: ScheduleLifecycleBulkConfirmModalProps) => {
    const [resumeDate, setResumeDate] = useState<string>(
        getScheduleResumeDefaultDate,
    );
    const [displayed, setDisplayed] =
        useState<ScheduleBulkConfirmTarget | null>(null);
    if (target && target !== displayed) {
        setDisplayed(target);
    }
    const verb = displayed?.verb;
    const isDatedResume =
        verb === ScheduleLifecycleVerb.resume &&
        displayed?.hasRatableSchedule === true;
    const {
        lockDate,
        isResolved: isLockResolved,
        isError: isLockError,
    } = useLockDateState(displayed?.entityId);
    const isLockKnown = isLockResolved || isLockError;
    const firstUnlockedDate = getFirstUnlockedDateOnly(lockDate);
    useEffect(() => {
        if (displayed?.verb === ScheduleLifecycleVerb.resume) {
            setResumeDate(getScheduleResumeDefaultDate());
        }
    }, [displayed]);
    useEffect(() => {
        if (isDatedResume) {
            setResumeDate((current) =>
                clampToUnlocked(current, firstUnlockedDate),
            );
        }
    }, [isDatedResume, firstUnlockedDate]);

    const renderQuestion = () => {
        if (!displayed) {
            return null;
        }
        const body = isDatedResume ? (
            <ScheduleResumeDateField
                body={displayed.body}
                value={resumeDate}
                minDate={firstUnlockedDate}
                onChange={setResumeDate}
            />
        ) : (
            <span>{displayed.body}</span>
        );
        return (
            <VStack gap={12}>
                {body}
                {displayed.skippedWarning && (
                    <span>{displayed.skippedWarning}</span>
                )}
            </VStack>
        );
    };

    return (
        <ConfirmationModal
            show={target !== null}
            title={
                displayed
                    ? formatScheduleBulkLifecycleTitle({
                          verb: displayed.verb,
                          count: displayed.count,
                      })
                    : ""
            }
            question={renderQuestion()}
            yes={
                verb ? SCHEDULE_BULK_LIFECYCLE_CONFIRM_LABELS[verb] : "Confirm"
            }
            confirmDisabled={
                isDatedResume &&
                (!isLockKnown || isLockedByDateOnly(lockDate, resumeDate))
            }
            alternative={
                verb === ScheduleLifecycleVerb.delete
                    ? {
                          label: SCHEDULE_DELETE_REMOVE_LABEL,
                          variant: "destructive",
                          onConfirm: () =>
                              onConfirm({
                                  verb: ScheduleLifecycleVerb.delete,
                                  journalEntryMode:
                                      ScheduleDeleteJournalEntryMode.remove,
                              }),
                      }
                    : undefined
            }
            onConfirm={() =>
                verb
                    ? onConfirm(buildConfirmation(verb, resumeDate))
                    : Promise.resolve()
            }
            no={SCHEDULE_OPTIONS_BACK_LABEL}
            onReject={onHide}
            onHide={onHide}
            onExited={() => setDisplayed(null)}
        />
    );
};
